import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const config = window.SGRConfig;
const SGR_PROPERTY = '_sgr';

if (config?.enabled && config?.variantId) {
  const sgrVariantId = Number(config.variantId);
  let syncPromise = null;
  let scheduled = null;
  const internalEvents = new WeakSet();

  const getSectionIds = () => {
    const ids = [];
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId && !ids.includes(component.dataset.sectionId)) {
        ids.push(component.dataset.sectionId);
      }
      if (ids.length === 5) break;
    }
    return ids;
  };

  const getEligibilityFromDom = () => {
    const eligibility = new Map();
    for (const row of document.querySelectorAll('[data-sgr-eligible][data-key]')) {
      if (row instanceof HTMLElement && row.dataset.key) {
        eligibility.set(row.dataset.key, row.dataset.sgrEligible === 'true');
      }
    }
    return eligibility;
  };

  const fetchCart = async () => {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`SGR cart fetch failed (${response.status})`);
    return response.json();
  };

  const desiredSgrQuantity = (cart) => {
    const eligibility = getEligibilityFromDom();
    return cart.items.reduce((total, item) => {
      if (Number(item.variant_id) === sgrVariantId) return total;
      const markedByProperty = item.properties?.[SGR_PROPERTY] === '1' || item.properties?.[SGR_PROPERTY] === true;
      const markedByLiquid = eligibility.get(item.key);
      const isEligible = markedByLiquid === undefined ? markedByProperty : markedByLiquid;
      return isEligible ? total + Number(item.quantity || 0) : total;
    }, 0);
  };

  const cartIsSynced = (cart, desiredQuantity) => {
    const depositLines = cart.items.filter((item) => Number(item.variant_id) === sgrVariantId);
    if (desiredQuantity === 0) return depositLines.length === 0;
    return depositLines.length === 1 && Number(depositLines[0].quantity) === desiredQuantity;
  };

  const updateSgrLine = async (cart, desiredQuantity) => {
    const depositLines = cart.items.filter((item) => Number(item.variant_id) === sgrVariantId);
    const updates = {};

    if (depositLines.length > 0) {
      updates[depositLines[0].key] = desiredQuantity;
      for (const duplicate of depositLines.slice(1)) updates[duplicate.key] = 0;
    } else if (desiredQuantity > 0) {
      // update.js can add a variant that isn't in the cart yet. Using the variant ID here
      // keeps the response as a full cart object, which the Horizon event system expects.
      updates[String(sgrVariantId)] = desiredQuantity;
    } else {
      return cart;
    }

    const sectionIds = getSectionIds();
    const body = {
      updates,
      sections_url: window.location.pathname + window.location.search,
    };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    const eventLines = depositLines.length
      ? depositLines.map((item, index) => ({ id: item.key, quantity: index === 0 ? desiredQuantity : 0 }))
      : [{ merchandiseId: String(sgrVariantId), quantity: desiredQuantity }];

    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();
    if (!response.ok || data.errors || data.status) {
      throw new Error(data.description || data.message || data.errors || `SGR cart update failed (${response.status})`);
    }

    // If the line was just created via update.js it has no private property. That is fine:
    // eligibility is stored on the actual products, while the SGR line is identified by variant ID.
    const resolved = Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: {
        sections: data.sections,
        items: data.items,
        itemCount: data.item_count,
        source: 'sgr-sync',
        didError: false,
      },
    });

    const event = new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: eventLines,
      promise: resolved,
    });
    internalEvents.add(event);
    document.dispatchEvent(event);

    return data;
  };

  const syncNow = async () => {
    if (syncPromise) return syncPromise;

    syncPromise = (async () => {
      const cart = await fetchCart();
      const desiredQuantity = desiredSgrQuantity(cart);
      if (cartIsSynced(cart, desiredQuantity)) return cart;
      return updateSgrLine(cart, desiredQuantity);
    })().finally(() => {
      syncPromise = null;
    });

    return syncPromise;
  };

  const scheduleSync = () => {
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(async () => {
      scheduled = null;
      try {
        // Give Horizon one paint to morph the cart sections first. This also lets the
        // Liquid metafield marker serve as a fallback for legacy/unmarked cart lines.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        await syncNow();
      } catch (error) {
        console.error('[SGR]', error);
      }
    }, 0);
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (internalEvents.has(event)) return;
    if (event.promise) {
      event.promise.then(scheduleSync).catch(() => {});
    } else {
      scheduleSync();
    }
  });

  // Checkout is a regular POST to the cart with the submitter's `name=checkout`.
  // An explicitly disabled submitter is omitted from that POST, so Shopify
  // reloads the cart instead of sending the customer to checkout.
  let checkoutInProgress = false;

  document.addEventListener(
    'submit',
    async (event) => {
      if (!(event.target instanceof HTMLFormElement) || !event.target.matches('.cart-form')) return;
      const submitter = event.submitter;
      if (!(submitter instanceof HTMLButtonElement) || submitter.name !== 'checkout') return;

      // The second submission must be handled natively by Shopify.
      if (event.target.dataset.sgrSubmitting === 'true') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (checkoutInProgress) return;
      checkoutInProgress = true;

      const originalForm = event.target;
      const isDrawer = Boolean(originalForm.closest('#cart-drawer'));
      const rootSelector = isDrawer ? '#cart-drawer' : '#MainContent';
      submitter.disabled = true;

      let activeForm = originalForm;
      let activeSubmitter = submitter;

      try {
        await syncNow();

        // Updating the SGR line may replace the cart markup (Horizon morph).
        // Use the current live form and checkout button if that happened.
        if (!activeForm.isConnected) {
          activeForm = document.querySelector(`${rootSelector} form.cart-form`);
        }
        if (!activeSubmitter.isConnected || activeSubmitter.form !== activeForm) {
          activeSubmitter = document.querySelector(`${rootSelector} button.cart__checkout-button[name="checkout"]`);
        }
        if (
          !(activeForm instanceof HTMLFormElement) ||
          !(activeSubmitter instanceof HTMLButtonElement) ||
          activeSubmitter.form !== activeForm
        ) {
          throw new Error('The checkout form was replaced during SGR synchronization.');
        }

        // Important: restore the button BEFORE requestSubmit, otherwise the
        // `checkout` parameter is missing and Shopify sends users back to /cart.
        activeSubmitter.disabled = false;
        activeForm.dataset.sgrSubmitting = 'true';
        activeForm.requestSubmit(activeSubmitter);
      } catch (error) {
        console.error('[SGR] Checkout blocked because the guarantee could not be synchronized.', error);
        originalForm.removeAttribute('data-sgr-submitting');
        if (activeForm instanceof HTMLFormElement) activeForm.removeAttribute('data-sgr-submitting');
        submitter.disabled = false;
        if (activeSubmitter instanceof HTMLButtonElement) activeSubmitter.disabled = false;
        alert('Nu am putut actualiza garanția SGR. Te rugăm să reîncerci înainte de checkout.');
      } finally {
        checkoutInProgress = false;
      }
    },
    true
  );

  document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
  window.addEventListener('pageshow', scheduleSync);
}
